import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import L from 'leaflet';
import type { Position } from '../../../types';
import { clampScale, DRAG_THRESHOLD_PX } from './markerDom';

interface Options {
  map: L.Map;
  selectedUidsRef: MutableRefObject<ReadonlySet<string>>;
  positionByUidRef: MutableRefObject<Map<string, Position>>;
  positionUidToElRef: MutableRefObject<Map<string, HTMLElement>>;
  movePositionsGroupRef: MutableRefObject<((anchorUid: string, newAnchorCoords: [number, number]) => void) | undefined>;
  toggleInSelectionRef: MutableRefObject<((uid: string) => void) | undefined>;
  selectPositionRef: MutableRefObject<((uid: string) => void) | undefined>;
}

export function useMarkerInteraction({
  map,
  selectedUidsRef,
  positionByUidRef,
  positionUidToElRef,
  movePositionsGroupRef,
  toggleInSelectionRef,
  selectPositionRef,
}: Options) {
  const attachDragOrSelect = useCallback((
    el: HTMLElement,
    uid: string,
    getCurrentCoords: () => [number, number],
  ) => {
    // Pointer events (vs mouse events) are required for touch drag to work
    // on phones/tablets. We capture the pointer on the marker element so the
    // drag stream continues even if the finger leaves the marker bounds.
    el.addEventListener('pointerdown', (e) => {
      if (!e.isPrimary) return;
      if (e.button !== undefined && e.button !== 0) return; // ignore right/middle mouse
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startY = e.clientY;
      const pointerId = e.pointerId;
      // Captured at pointerdown so the release path sees the same modifiers
      // even if the OS drops them mid-drag (rare, but defensive).
      const additive = e.shiftKey || e.ctrlKey || e.metaKey;
      let moved = false;
      let liveCoords = getCurrentCoords();
      const zoom = map.getZoom();
      const scale = clampScale(zoom);

      // Determine multi-drag at pointerdown: only when the dragged marker is
      // already part of a multi-selection.
      const currentSelectedUids = selectedUidsRef.current;
      const isMultiDrag = currentSelectedUids.has(uid) && currentSelectedUids.size > 1;

      // Pre-compute anchor and peer layer points so onMove can apply a pure
      // delta without repeating the projection on every pointer event.
      const originalAnchorCoords = getCurrentCoords();
      const originalAnchorLayerPoint = map.latLngToLayerPoint(
        L.latLng(originalAnchorCoords[0], originalAnchorCoords[1]),
      );
      type PeerEntry = { el: HTMLElement; origLayerPoint: L.Point };
      const peerEntries: PeerEntry[] = [];
      if (isMultiDrag) {
        const posMap = positionByUidRef.current;
        for (const selUid of currentSelectedUids) {
          if (selUid === uid) continue;
          const pos = posMap.get(selUid);
          if (!pos) continue;
          const peerEl = positionUidToElRef.current.get(selUid);
          if (!peerEl) continue;
          peerEntries.push({
            el: peerEl,
            origLayerPoint: map.latLngToLayerPoint(L.latLng(pos.coordinates[0], pos.coordinates[1])),
          });
        }
      }

      try {
        el.setPointerCapture(pointerId);
      } catch {
        // setPointerCapture can throw if the pointer was already released
        // (rare; happens with synthetic test events). Drag still works via
        // document-level listeners below.
      }

      const onMove = (me: PointerEvent) => {
        if (me.pointerId !== pointerId) return;
        const dx = me.clientX - startX;
        const dy = me.clientY - startY;
        if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        if (!moved) {
          moved = true;
          el.dataset.dragging = 'true';
          if (isMultiDrag) {
            for (const { el: peerEl } of peerEntries) {
              peerEl.dataset.dragging = 'true';
            }
          }
        }
        const rect = map.getContainer().getBoundingClientRect();
        const containerPoint = L.point(me.clientX - rect.left, me.clientY - rect.top);
        const latLng = map.containerPointToLatLng(containerPoint);
        liveCoords = [latLng.lat, latLng.lng];
        const layerPoint = map.latLngToLayerPoint(latLng);
        el.style.transform = `translate(${layerPoint.x}px, ${layerPoint.y}px) translate(-50%,-50%) scale(${scale})`;
        if (isMultiDrag) {
          const ldx = layerPoint.x - originalAnchorLayerPoint.x;
          const ldy = layerPoint.y - originalAnchorLayerPoint.y;
          for (const { el: peerEl, origLayerPoint } of peerEntries) {
            peerEl.style.transform = `translate(${origLayerPoint.x + ldx}px, ${origLayerPoint.y + ldy}px) translate(-50%,-50%) scale(${scale})`;
          }
        }
      };

      const finish = (committed: boolean) => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onCancel);
        try {
          if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
        } catch {
          // No-op: capture may have already been released.
        }
        delete el.dataset.dragging;
        if (isMultiDrag) {
          for (const { el: peerEl } of peerEntries) {
            delete peerEl.dataset.dragging;
          }
        }
        if (!committed) return;
        if (moved) {
          movePositionsGroupRef.current?.(uid, liveCoords);
        } else if (additive) {
          toggleInSelectionRef.current?.(uid);
        } else {
          selectPositionRef.current?.(uid);
        }
      };

      const onUp = (up: PointerEvent) => {
        if (up.pointerId !== pointerId) return;
        finish(true);
      };
      const onCancel = (c: PointerEvent) => {
        if (c.pointerId !== pointerId) return;
        finish(false);
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onCancel);
    });
  // Refs are stable — intentionally omitted from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return { attachDragOrSelect };
}
